import { getSelectStyles, getActionSelectStyles } from '../../utility/selectStyles';
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {FaArrowLeft, FaPlus, FaRegCopy, FaCheck} from 'react-icons/fa';
import InfoBanner from '../InfoBanner';
import { useToast } from '../../components/ToastContext';
import Modal from '../../components/Modal';
import Select from 'react-select';
import axiosInstance from '../../utility/axiosInstance';
import { handleAxiosError } from '../../helpers/handleAxiosError';
import { fetchInstances } from '../../store/slices/instancesSlice';
import { fetchSubscriptionInfo } from '../../store/slices/paymentSlice';
import DNSFormModal from './DNSFormModal';
import DNSDetailsView from './DNSDetailsView';
import {AppDispatch, RootState} from "../../store/store";
import { useConfirm } from "../../components/ConfirmContext";
import { useLimitedTrial } from "../../hooks/useLimitedTrial";
import { deleteDnsEntry } from "../../helpers/deleteDnsEntry";

interface DNSRecord {
    id: number;
    name: string;
    port: number;
}

interface DNSManagementViewProps {
    instanceId: string;
    instanceDns: string;
    instances: any[];
    onBack: () => void;
}

const CopyDnsButton: React.FC<{ dnsName: string }> = ({ dnsName }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        const url = dnsName.startsWith('http') ? dnsName : `https://${dnsName}`;
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="command-block dns-copy-block">
            <code>https://{dnsName}</code>
            <button
                className={`copy-inner-btn ${copied ? 'copied' : ''}`}
                onClick={(e) => { e.stopPropagation(); handleCopy(); }}
                title="Copy DNS name"
            >
                {copied ? <FaCheck className="icon-check" /> : <FaRegCopy className="icon-copy" />}
            </button>
        </div>
    );
};

const DNSManagementView: React.FC<DNSManagementViewProps> = ({
                                                                 instanceId,
                                                                 instanceDns,
                                                                 instances,
                                                                 onBack,
                                                             }) => {
    const { confirm } = useConfirm();
    const { showToast } = useToast();
    const dispatch = useDispatch<AppDispatch>();
    const isTrial = useLimitedTrial();
    const subscription = useSelector((state: RootState) => state.payment.subscription);
    const subscriptionStatus = useSelector((state: RootState) => state.payment.status);
    const [filteredDNS, setFilteredDNS] = useState<DNSRecord[]>([]);
    const [editingDNS, setEditingDNS] = useState<DNSRecord | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [proxyHosts, setProxyHosts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedProxyHost, setSelectedProxyHost] = useState<any | null>(null);

    const refreshProxyHosts = async () => {
        try {
            const response = await axiosInstance.get('/v1/dns/list');
            setProxyHosts(response.data);
            if (selectedProxyHost) {
                const updated = response.data.find((ph: any) => ph.id === selectedProxyHost.id);
                if (updated) setSelectedProxyHost(updated);
            }
        } catch (err) {
            console.error("Error listing proxy hosts:", err);
        }
    };

    useEffect(() => {
        if (instanceDns) {
            const dnsEntries = instanceDns.split(",").filter(e => e.trim().length > 0);
            const dnsObjects = dnsEntries.map((entry: string) => {
                const parts = entry.split(":");
                if (parts.length < 3) return null;
                const [id, name, port] = parts;
                return { id: Number(id), name, port: Number(port) };
            }).filter((dn): dn is DNSRecord => dn !== null && !isNaN(dn.id));
            setFilteredDNS(dnsObjects);
        } else {
            setFilteredDNS([]);
        }
    }, [instanceDns]);

    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        dispatch(fetchInstances());
        refreshProxyHosts();
        if (subscriptionStatus === 'idle') {
            dispatch(fetchSubscriptionInfo());
        }

        // Poll until DNS data is available (e.g. instance was just created)
        pollIntervalRef.current = setInterval(() => {
            dispatch(fetchInstances());
        }, 5000);

        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        };
    }, []);

    // Stop polling once DNS data has loaded
    useEffect(() => {
        if (filteredDNS.length > 0 && pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    }, [filteredDNS]);

    const isBillingInvalid = subscription?.status === 'past_due' || subscription?.status === 'canceled' || subscription?.status === 'for_deletion';
    // No-card trial: one public DNS per instance. Block a 2nd add.
    const trialDnsLimitReached = isTrial && filteredDNS.length >= 1;
    const createBlocked = isBillingInvalid || trialDnsLimitReached;
    const createBlockedMsg = isBillingInvalid
        ? "Please resolve your billing to create new DNS records."
        : "The limited trial allows one DNS per instance. Connect a card to add more.";

    const handleAction = (action: string, dn: DNSRecord) => {
        if (action === 'update') {
            setEditingDNS(dn);
            setShowModal(true);
        } else if (action === 'delete') {
            handleDeleteDNS(dn.id);
        }
    };

    const handleDeleteDNS = async (proxyHostId: number) => {
        const proceed = await confirm({
            title: 'Delete DNS Record',
            message: 'Delete this DNS record?',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
        });
        if (!proceed) return;
        setLoading(true);
        try {
            // The instance's record of the name, then the name (shared with the domains page: helpers/deleteDnsEntry).
            await deleteDnsEntry(proxyHostId, instances.find(inst => inst.id === instanceId));

            showToast('DNS deleted!', 'success');
            dispatch(fetchInstances());
        } catch (err) {
            showToast('Failed to delete DNS!', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateDNS = () => {
        setEditingDNS(null);
        setShowModal(true);
    };

    const handleRowClick = (dn: DNSRecord) => {
        const ph = proxyHosts.find(p => p.id === dn.id);
        if (ph) {
            setSelectedProxyHost(ph);
        }
    };

    if (selectedProxyHost) {
        return (
            <DNSDetailsView
                proxyHost={selectedProxyHost}
                onBack={() => setSelectedProxyHost(null)}
                onRefresh={refreshProxyHosts}
                isBillingInvalid={isBillingInvalid}
            />
        );
    }

    return (
        <div className="dns-management-view">
            <button className="back-button" onClick={onBack}>
                <FaArrowLeft /> Back to Instances
            </button>

            <div className="page-header">
                <p className="page-subtitle">
                    Manage custom domains and port mappings for your instance
                </p>
                <InfoBanner id="dns-management-info">
                    <span>Domain name is the access point for your application.</span>
                    <span>Port next to domain name shows which port is targeted. This is the port your application should be served on.</span>
                </InfoBanner>
            </div>

            <button
                className={`create-button ${createBlocked ? 'disabled' : ''}`}
                onClick={createBlocked ? () => showToast(createBlockedMsg, "error") : handleCreateDNS}
                disabled={createBlocked}
                title={createBlocked ? createBlockedMsg : undefined}
            >
                <FaPlus /> <span>Create DNS</span>
            </button>
            {trialDnsLimitReached && !isBillingInvalid && (
                <p className="page-subtitle" style={{ color: 'var(--text-muted)', marginTop: '8px' }}>
                    The limited trial allows one DNS per instance. Connect a card to add more.
                </p>
            )}

            <DNSFormModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                editingDNS={editingDNS}
                instanceId={instanceId}
                instances={instances}
                proxyHosts={proxyHosts}
                existingDNS={filteredDNS}
                loading={loading}
                setLoading={setLoading}
            />

            
            <div className="desktop-view">
                <div className="table-container">
                    <table className="keypairs-table">
                        <thead>
                        <tr>
                            <th>Domain Name</th>
                            <th>Port</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredDNS.length > 0 ? (
                            filteredDNS.map((dn) => (
                                <tr key={dn.id} onClick={() => handleRowClick(dn)} className="clickable-row">
                                    <td className="keypair-name">
                                        <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-block', maxWidth: '100%' }}>
                                            <CopyDnsButton dnsName={dn.name} />
                                        </div>
                                    </td>
                                    <td>{dn.port}</td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <Select
                                            options={[
                                                { value: 'update', label: 'Update DNS' },
                                                { value: 'delete', label: 'Delete DNS' }
                                            ]}
                                            onChange={(opt) => opt && handleAction(opt.value, dn)}
                                            placeholder="Actions"
                                            isSearchable={false}
                                            menuPortalTarget={document.body}
                                            styles={getActionSelectStyles()}
                                        />
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={3} className="empty-state">
                                    No DNS records yet
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            </div>

            
            <div className="mobile-view">
                {filteredDNS.length > 0 ? (
                    <div className="cards-container">
                        {filteredDNS.map((dn) => (
                            <div key={dn.id} className="keypair-card" onClick={() => handleRowClick(dn)} style={{ cursor: 'pointer' }}>
                                <div className="card-header">
                                    <h3 className="card-title">
                                        <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-block', maxWidth: '100%' }}>
                                            <CopyDnsButton dnsName={dn.name} />
                                        </div>
                                    </h3>
                                    <div style={{ width: '100%', marginTop: '12px' }} onClick={(e) => e.stopPropagation()}>
                                        <Select
                                            options={[
                                                { value: 'update', label: 'Update DNS' },
                                                { value: 'delete', label: 'Delete DNS' }
                                            ]}
                                            onChange={(opt) => opt && handleAction(opt.value, dn)}
                                            placeholder="Actions"
                                            isSearchable={false}
                                            menuPortalTarget={document.body}
                                            styles={getActionSelectStyles()}
                                        />
                                    </div>
                                </div>
                                <div className="card-content">
                                    <div className="fingerprint">
                                        <span className="label">Port:</span>
                                        <span className="value">{dn.port}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="empty-state">
                        <p>No DNS records yet</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DNSManagementView;
